import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  ArrowRight, 
  Info, 
  Zap, 
  Shield, 
  Droplet, 
  Sparkles, 
  Car, 
  Clock, 
  Award 
} from 'lucide-react';

// Import images
import exteriorImage from './images/buffer2.jpg';
import interiorImage from './images/interiorDetail.jpg';
import ceramicImage from './images/ceramicCoating.jpg';

const ServicesPage = ({ isMobile }) => {
  // State for tab navigation and FAQ expansion
  const [activeTab, setActiveTab] = useState('full');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Service definitions with proper image mapping
  const serviceContent = {
    full: {
      title: "Full Detailing",
      description: "Our comprehensive full detailing service combines both interior and exterior treatments to fully restore your vehicle's appearance. Recommended for vehicles that haven't been professionally detailed in 6+ months, before selling your car, or after winter season to remove salt and grime buildup.",
      image: ceramicImage
    },
    exterior: {
      title: "Exterior Detailing",
      description: "Our exterior detailing focuses on restoring your vehicle's paint, trim, wheels, and glass to a showroom shine. Recommended for vehicles regularly exposed to harsh elements like sun, bird droppings, or tree sap, or if you notice swirl marks and minor scratches.",
      image: exteriorImage
    },
    interior: {
      title: "Interior Detailing",
      description: "Our interior detailing service deep cleans all inside surfaces, removing stains, odors, and built-up grime from your vehicle's cabin. Recommended for allergy sufferers, after long road trips, for families with young children, or if you've recently purchased a used vehicle.",
      image: interiorImage
    }
  };

  // Service package data
  const servicePackages = {
    full: [
      {
        id: 'silver',
        title: 'Silver Package',
        tagline: 'Quick Refresh',
        price: 195,
        timeEstimate: '1-2 hours',
        idealFor: 'Regular maintenance',
        features: {
          'INTERIOR': [
            'Interior Vacuum',
            'Wipe all surfaces',
            'Spot treat stains',
            'Clean windows & mirrors',
            'Clean door jambs',
            'Detail floor mats',
            'Clean trunk'
          ],
          'EXTERIOR': [
            'Hand wash',
            'Clean wheels & tires',
            'Apply wax (3-month protection)'
          ]
        }
      },
      {
        id: 'gold',
        title: 'Gold Package',
        tagline: 'Most Popular',
        price: 275,
        isPopular: true,
        timeEstimate: '2-3 hours',
        idealFor: 'Seasonal detailing',
        features: {
          'INTERIOR': [
            'Deep vacuum with crevice tools',
            'Clean & condition all surfaces',
            'Stain treatment',
            'Windows & mirrors (streak-free)',
            'Protect plastic & vinyl surfaces',
            'Condition floor mats',
            'Detail trunk & door jambs'
          ],
          'EXTERIOR': [
            'Spot polish paint correction',
            'Two-bucket hand wash',
            'Clay bar treatment',
            'Clean & dress wheels & tires',
            'Clean wheel wells',
            'Premium wax application'
          ]
        }
      },
      {
        id: 'diamond',
        title: 'Diamond Package',
        tagline: 'Premium Service',
        price: 399,
        timeEstimate: '4-6 hours',
        idealFor: 'Show-quality results',
        features: {
          'INTERIOR': [
            'Shampoo seats & carpets',
            'Deep extraction cleaning',
            'Complete surface deep clean',
            'Stain removal treatment',
            'UV protection for surfaces',
            'Full interior sanitization',
            'Leather conditioning (if applicable)',
            'Complete detail of all crevices'
          ],
          'EXTERIOR': [
            'Single-stage paint correction',
            'Premium hand wash',
            'Clay bar decontamination',
            'Wheel well cleaning',
            'Tire & trim restoration',
            'Clean all exterior gaps & jambs',
            'High-end carnauba wax finish'
          ]
        }
      }
    ],
    exterior: [
      {
        id: 'classic_exterior',
        title: 'Express Exterior',
        tagline: 'Basic Clean',
        price: 168,
        timeEstimate: '1-2 hours',
        idealFor: 'Monthly maintenance',
        features: [
          'Hand wash & dry',
          'Wheel cleaning',
          'Tire shine application',
          'Windows & mirrors',
          'Basic wax protection',
          'Exterior plastic treatment'
        ]
      },
      {
        id: 'wax_polish',
        title: 'Paint Enhancement',
        tagline: 'Popular Choice',
        price: 289,
        isPopular: true,
        timeEstimate: '2-3 hours',
        idealFor: 'Removing light swirls',
        features: [
          'Single-stage polish',
          'Paint decontamination',
          'Clay bar treatment',
          'Premium hand wash',
          'Wheel & tire detailing',
          'Trim restoration',
          '3-month wax protection'
        ]
      },
      {
        id: 'cut_polish',
        title: 'Paint Correction',
        tagline: 'Show Quality',
        price: 439,
        timeEstimate: '4-6 hours',
        idealFor: 'Removing deep scratches',
        features: [
          'Two-stage correction process',
          'Heavy swirl & scratch removal',
          'Complete decontamination',
          'Premium wash & clay treatment',
          'Wheel & trim restoration',
          'High-end carnauba wax finish',
          'Glass polishing'
        ]
      }
    ],
    interior: [
      {
        id: 'classic_interior',
        title: 'Basic Interior',
        tagline: 'Essential Clean',
        price: 186,
        timeEstimate: '1-2 hours',
        idealFor: 'Light interior cleaning',
        features: [
          'Thorough vacuuming',
          'Dust & wipe all surfaces',
          'Clean windows & mirrors',
          'Floor mat cleaning',
          'Trunk vacuuming',
          'Basic odor elimination'
        ]
      },
      {
        id: 'comfort_interior',
        title: 'Deep Interior',
        tagline: 'Most Popular',
        price: 239,
        isPopular: true,
        timeEstimate: '2-3 hours',
        idealFor: 'Thorough cleaning',
        features: [
          'Deep vacuum with crevice tools',
          'Complete surface cleaning',
          'Stain treatment',
          'Console & cup holder detail',
          'Door panel deep clean',
          'Dashboard conditioning',
          'Air vent cleaning',
          'Premium odor elimination'
        ]
      },
      {
        id: 'deep_clean',
        title: 'Premium Interior',
        tagline: 'Complete Restoration',
        price: 289,
        timeEstimate: '3-4 hours',
        idealFor: 'Like-new results',
        features: [
          'Carpet & upholstery shampooing',
          'Leather cleaning & conditioning',
          'Stain extraction treatment',
          'Complete surface restoration',
          'Headliner cleaning',
          'Sanitization treatment',
          'UV protection for all surfaces',
          'Long-lasting odor elimination'
        ]
      }
    ]
  };

  // FAQs
  const faqs = [
    {
      question: "What's the difference between the packages?",
      answer: "Silver is our quick refresh service for well-maintained vehicles. Gold offers deeper cleaning with clay bar and spot polish, perfect for seasonal detailing. Diamond provides comprehensive restoration with paint correction and upholstery shampooing, ideal for neglected vehicles or show-quality results."
    },
    {
      question: "How long do services take?",
      answer: "Service times vary by package: Silver (1-2 hours), Gold (2-3 hours), and Diamond (4-6 hours). We'll provide a specific time estimate when booking your appointment."
    },
    {
      question: "What vehicles do you service?",
      answer: "We service all passenger vehicles including sedans, SUVs, trucks, and luxury vehicles. There may be an additional charge for oversized vehicles or those requiring special care."
    },
    {
      question: "How should I prepare my vehicle?",
      answer: "Simply remove personal items and valuables. No pre-cleaning necessary—we'll handle everything from there!"
    },
    {
      question: "How often should I detail my vehicle?",
      answer: "For optimal results, we recommend a full detail every 3-4 months with maintenance washes in between. Frequency depends on your driving conditions and vehicle usage."
    }
  ];

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Toggle FAQ expansion
  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Get gradient color based on package tier
  const getGradientColor = (tagline) => {     
    if (tagline.includes('Premium') || tagline.includes('Show')) return 'from-indigo-600 to-blue-700';
    if (tagline.includes('Most Popular') || tagline.includes('Popular')) return 'from-blue-500 to-cyan-400';
    return 'from-blue-400 to-teal-400';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Container */}
      <div className="container mx-auto px-6 py-24 max-w-8xl">
        {/* Header Section */}
        <header className="mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Professional Car Detailing</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Customized detailing packages to restore and maintain your vehicle's appearance
          </p>
        </header>

        {/* Service Type Selection - Clean Tab Interface */}
        <div className="bg-white rounded-xl shadow-sm mb-10 overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => handleTabChange('full')}
              className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 font-medium transition-all ${
                activeTab === 'full' 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Sparkles className="h-5 w-5" />
              <span>Full Detail</span>
            </button>
            <button
              onClick={() => handleTabChange('exterior')}
              className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 font-medium transition-all ${
                activeTab === 'exterior' 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Car className="h-5 w-5" />
              <span>Exterior Only</span>
            </button>
            <button
              onClick={() => handleTabChange('interior')}
              className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 font-medium transition-all ${
                activeTab === 'interior' 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Droplet className="h-5 w-5" />
              <span>Interior Only</span>
            </button>
          </div>

          {/* Service Hero Area with Image */}
          <div className="p-6 md:p-8 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <h2 className="text-2xl font-bold mb-3 text-gray-800">
                {serviceContent[activeTab].title}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {serviceContent[activeTab].description}
              </p>
            
            </div>
            <div className="md:col-span-2 rounded-lg overflow-hidden shadow-sm h-48 md:h-64">
              <img 
                src={serviceContent[activeTab].image} 
                alt={serviceContent[activeTab].title} 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Service Packages Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
            {serviceContent[activeTab].title} Packages
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {servicePackages[activeTab].map((pkg) => {
              const gradientColors = getGradientColor(pkg.tagline);
              return (
                <div
                  key={pkg.id}
                  className={`bg-white rounded-xl overflow-hidden transition-all duration-300 shadow hover:shadow-md ${
                    pkg.isPopular ? 'ring-2 ring-blue-500 relative transform md:scale-105 z-10' : ''
                  }`}
                >
                  {pkg.isPopular && (
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  <div className={`bg-gradient-to-r ${gradientColors} text-white p-5`}>
                    <h3 className="text-xl font-bold">{pkg.title}</h3>
                    <div className="mt-2 mb-1">
                      <span className="text-3xl font-bold">${pkg.price}</span>
                    </div>
                    <span className="text-sm opacity-90">{pkg.tagline}</span>
                  </div>

                  <div className="p-5">
                    {/* Quick Info */}
                    <div className="mb-4 space-y-2 border-b border-gray-100 pb-4">
                      <div className="flex items-start">
                        <Clock className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Service Time</p>
                          <p className="text-sm text-gray-600">{pkg.timeEstimate}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Best For</p>
                          <p className="text-sm text-gray-600">{pkg.idealFor}</p>
                        </div>
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="mb-5">
                      <h4 className="font-medium mb-3 text-gray-800 text-sm uppercase">What's Included</h4>
                      {typeof pkg.features === 'object' && !Array.isArray(pkg.features) ? (
                        // Features with categories
                        Object.entries(pkg.features).map(([category, items]) => (
                          <div key={category} className="mb-3">
                            <h5 className="text-xs font-bold text-gray-500 mb-1">{category}</h5>
                            <ul className="space-y-1.5">
                              {items.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-sm">
                                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-700">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      ) : (
                        // Simple features list
                        <ul className="space-y-1.5">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Book Button */}
                    <Link to={`/book?service=${pkg.id}&title=${encodeURIComponent(pkg.title)}&price=${pkg.price}&category=${activeTab}`}>
                      <button className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 flex items-center justify-center">
                        Book This Package
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Premium Products</h3>
            <p className="text-gray-600 text-sm">
              We use professional-grade, eco-friendly products that protect both your vehicle and the environment while delivering superior results.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Award className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Expert Technicians</h3>
            <p className="text-gray-600 text-sm">
              Our certified detailers have years of experience and training in professional detailing techniques and use modern equipment for best results.
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Car className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Mobile Convenience</h3>
            <p className="text-gray-600 text-sm">
              We come to your home or office with all needed equipment, saving you time and hassle while still delivering a professional detailing experience.
            </p>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="bg-white rounded-xl shadow-sm p-6 mb-12">
          <h2 className="text-xl font-bold mb-6 text-center">Frequently Asked Questions</h2>

          <div className="space-y-3 max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`border rounded-lg overflow-hidden transition-all duration-200 ${
                  expandedFaq === index ? 'border-blue-200 shadow-sm' : 'border-gray-200'
                }`}
              >
                <button
                  className="w-full text-left p-4 flex justify-between items-center focus:outline-none"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-medium text-gray-800">{faq.question}</span>
                  <Info className={`h-5 w-5 ${expandedFaq === index ? 'text-blue-500' : 'text-gray-400'}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-4 pb-4 text-gray-600 border-t border-gray-100 pt-2 text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl shadow-md p-6 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to transform your vehicle?</h2>
          <p className="mb-5 max-w-2xl mx-auto text-sm">
            Book your detailing appointment today and experience the difference professional care makes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/book">
              <button className="px-6 py-2 bg-white text-blue-600 rounded-lg font-medium hover:shadow-md transition-all duration-300">
                Book Now
              </button>
            </Link>
            <Link to="/contact">
              <button className="px-6 py-2 bg-transparent border border-white text-white rounded-lg font-medium hover:bg-white/10 transition-all duration-300">
                Contact Us
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;